/**
 * Dates as the screen shows them, in the source's own clock. A card shows the
 * day ("Aug 21"); Trace shows the full timestamp with its offset ("Aug 21,
 * 2026, 14:05 (+02:00)"), absolute and never relative. Both read the offset
 * the source carried, so a message sent at 00:30 in Berlin is dated the day
 * its sender saw, in any host.
 */

type Offset = { readonly minutes: number; readonly label: string }

function offsetOf(iso: string): Offset {
  const m = /([+-])(\d{2}):(\d{2})$/.exec(iso)
  if (!m) return { minutes: 0, label: 'UTC' }
  const sign = m[1] === '-' ? -1 : 1
  return {
    minutes: sign * (Number(m[2]) * 60 + Number(m[3])),
    label: `${m[1]}${m[2]}:${m[3]}`,
  }
}

/** The instant shifted into its own offset, so UTC formatting shows local clock time. */
const shifted = (iso: string, offset: Offset): Date =>
  new Date(Date.parse(iso) + offset.minutes * 60_000)

/** "Aug 21" — the day, for a card's attribution and a signal's line. */
export function shortDate(iso: string): string {
  return shifted(iso, offsetOf(iso)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

/** "Aug 21, 2026, 14:05 (+02:00)" — absolute, with the source's offset, for Trace. */
export function longDate(iso: string): string {
  const offset = offsetOf(iso)
  const base = shifted(iso, offset).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: 'UTC',
  })
  return `${base} (${offset.label})`
}
